// ============================================================
// defence/flow - what a defender's answer DOES.
//
// No reaction passes every hit through; Dodge or Parry opens the chain.
// A shared prompt is claimed on the server first (multiplayer), and a
// finished chain - or a pass - hands its hits to `applyHits`, offers
// Furious Assault (solo), and clears the prompt.
// ============================================================
import { withDefenderMods } from '../../engine/defenceCalc';
import { diag, errorSummary } from '../../diagnostics/log';
import type { Tab, Target } from '../../types';
import { claimWithPatience } from './claim';
import { promptRefused } from './sync';
import { chainPayload, isChainDone, openChain, passThroughPayload, type ChainRules } from './chain';
import type { DefenceChainState, MultiplayerService, PendingAttackData } from './types';

type BonusPrompt = {
  talentName: string;
  slot: unknown;
  tabId: string;
  modifiedSkill: number;
  actorId: string | null;
} | null;

/** Where a resolved defence goes: the hits, the bonus prompt, the cleared prompt and chain. */
export interface ResolveSink {
  applyHits: (tabId: string, data: unknown) => void;
  sessionId?: string | null;
  clearPendingDefence?: ((tabId: string) => void) | null;
  setPendingAttack: (pa: PendingAttackData | null) => void;
  setPendingBonusAttack?: ((pba: BonusPrompt) => void) | null;
  setDefenceChain: (c: DefenceChainState | null) => void;
}

/** Everything answering a prompt reads and writes. */
export interface DefenceEnv extends ChainRules {
  sink: ResolveSink;
  pendingAttack: PendingAttackData | null;
  tabs: Tab[];
  playerId?: string | null;
  mp?: MultiplayerService | null;
  onReactionTaken?: ((tabId: string) => void) | null;
  setDefenceResolving: (v: boolean) => void;
}

/**
 * Phase 7: the Furious Assault prompt after the defence resolves. Solo
 * only - in multiplayer it routes via the attacker's Firestore listener.
 */
function offerFuriousAssault(env: ResolveSink, pa: PendingAttackData, tabId: string): void {
  if (pa.furAssaultEligible && env.setPendingBonusAttack && !env.sessionId) {
    env.setPendingBonusAttack({
      talentName: 'Furious Assault',
      slot: pa.slot,
      tabId,
      modifiedSkill: pa.modifiedSkill,
      actorId: (pa.actorId as string | null | undefined) ?? null,
    });
  }
}

/** Every hit lands, no chain: applied, Furious Assault offered, the prompt cleared. */
export function passThrough(env: ResolveSink, pa: PendingAttackData): void {
  env.applyHits(pa.tabId, passThroughPayload(pa));
  offerFuriousAssault(env, pa, pa.tabId);
  if (env.clearPendingDefence) env.clearPendingDefence(pa.tabId);
  env.setPendingAttack(null);
}

/**
 * The chain ends: its hits applied, Furious Assault offered, and the
 * prompt cleared - the local map in solo, the document in a session (see
 * `useDefenceSync.clearDefence`).
 */
export function finishChain(env: ResolveSink, chain: DefenceChainState): void {
  env.applyHits(chain.tabId, chainPayload(chain));
  offerFuriousAssault(env, chain.pendingAttackData, chain.tabId);
  if (env.clearPendingDefence) env.clearPendingDefence(chain.tabId);
  env.setPendingAttack(null);
  env.setDefenceChain(null);
}

/**
 * The target the defence resolves against. Fold the defender's custom
 * modifiers into the skills they modify HERE, not at the freeze, because
 * SOLO PLAY HAS NO FROZEN TARGET: `frozenTarget` is written only on the
 * multiplayer path, and the solo fallback is the live projection, which
 * deliberately does not carry these. `withDefenderMods` is idempotent.
 * The attacker's reaction exposures ride the prompt (v8.368.3); solo play
 * has no frozen target, so they are set on the live one here.
 */
function defenceTarget(pa: PendingAttackData, tabs: Tab[]): Target | null | undefined {
  const rawTarget = pa.frozenTarget || tabs.find((t) => t.id === pa.tabId)?.target;
  const reactionMods = (pa.attackerReactionMods as { dodge: number; parry: number } | null | undefined) ?? null;
  const folded = rawTarget ? withDefenderMods(rawTarget) : rawTarget;
  return folded && reactionMods ? { ...folded, attackerReactionMods: reactionMods } : folded;
}

/** One answer to a prompt. */
export interface DefenceChoice {
  choice: string;
  multiplayerDefence?: PendingAttackData | null;
  manualMod: number;
}

/**
 * Is this a SHARED prompt, claimed on the server before it is resolved? A
 * local one needs no claim - and must not await anything: solo resolution
 * runs synchronously inside the click, and callers rely on it.
 */
function isShared(env: DefenceEnv, req: DefenceChoice, tabId: string): boolean {
  // A hidden victim's prompt was never written (plan map-display, G1): there is nothing on the server to claim.
  const local = !!(req.multiplayerDefence as { localOnly?: boolean } | null | undefined)?.localOnly;
  if (!(env.sink.sessionId && req.multiplayerDefence && env.mp) || local) return false;
  // Nor was one the server refused (2026-10-30): it lives only here, and a claim would drop its hits.
  if (!promptRefused(tabId)) return true;
  diag.warn('defence', `prompt ${tabId} never reached the server - answered here`);
  return false;
}

/**
 * Claim a shared prompt. Returns false when the claim was lost (the prompt
 * is dropped); an error resolves locally.
 */
async function claimShared(
  env: DefenceEnv,
  pa: PendingAttackData,
  req: DefenceChoice & { claimAs: string; extra?: Record<string, unknown> },
  onError: (err: unknown) => void,
): Promise<boolean> {
  const { sessionId, setPendingAttack } = env.sink;
  env.setDefenceResolving(true);
  try {
    const claim = { sessionId: sessionId!, tabId: pa.tabId, playerId: env.playerId!, choice: req.claimAs };
    const result = await claimWithPatience(env.mp!, { ...claim, extra: req.extra });
    if (!result) {
      setPendingAttack(null);
      env.setDefenceResolving(false);
      return false;
    }
  } catch (err) {
    onError(err);
  }
  env.setDefenceResolving(false);
  return true;
}

/** 'none': claimed if shared, then every hit passes through. */
async function takeNoReaction(env: DefenceEnv, pa: PendingAttackData, req: DefenceChoice): Promise<void> {
  const onError = (err: unknown) => {
    console.error('[Defence] transaction error, resolving locally:', err);
    diag.error('defence', `claim of ${pa.tabId} threw - resolving locally`, errorSummary(err));
  };
  if (isShared(env, req, pa.tabId) && !(await claimShared(env, pa, { ...req, claimAs: 'none' }, onError))) return;
  passThrough(env.sink, pa);
}

/** Dodge or Parry: this IS a reaction being taken - claimed if shared, then the chain opens. */
async function startChain(env: DefenceEnv, pa: PendingAttackData, target: Target, req: DefenceChoice): Promise<void> {
  env.onReactionTaken?.(pa.tabId);
  const extra = req.manualMod ? { manualMod: req.manualMod } : undefined;
  const onError = (err: unknown) => console.error('[Defence] transaction error, resolving locally:', err);
  if (isShared(env, req, pa.tabId) && !(await claimShared(env, pa, { ...req, claimAs: 'chain', extra }, onError)))
    return;
  const chain = openChain(pa, target, req.choice, req.manualMod, env);
  if (isChainDone(chain)) finishChain(env.sink, chain);
  else env.sink.setDefenceChain(chain);
}

/** Handle the initial defence choice: 'dodge' and 'parry' start the chain, 'none' passes all hits through. */
export async function chooseDefence(env: DefenceEnv, req: DefenceChoice): Promise<void> {
  const pa = req.multiplayerDefence || env.pendingAttack;
  const ruling = req.manualMod ? ` ruling ${req.manualMod}` : '';
  diag.info('defence', `choice ${req.choice} on ${pa?.tabId ?? '?'}${ruling}`, {
    shared: !!req.multiplayerDefence,
    local: !!env.pendingAttack,
    resolvedBy: (req.multiplayerDefence as { resolvedBy?: unknown } | null | undefined)?.resolvedBy ?? null,
  });
  if (!pa) return;
  const target = defenceTarget(pa, env.tabs);
  if (!target) {
    env.sink.setPendingAttack(null);
    return;
  }
  if (req.choice === 'none') return takeNoReaction(env, pa, req);
  return startChain(env, pa, target, req);
}
